import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TablePaginationConfig } from 'antd/es/table';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import { api } from './api';
import { ChangeRequest } from './types';
import { formatDateTimeFull } from './constants';

const { Text, Paragraph } = Typography;

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Lav' },
  { value: 'MEDIUM', label: 'Mellem' },
  { value: 'HIGH', label: 'Høj' }
];

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'Ny' },
  { value: 'IN_PROGRESS', label: 'Under behandling' },
  { value: 'DONE', label: 'Afsluttet' },
  { value: 'REJECTED', label: 'Afvist' }
];

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'default',
  MEDIUM: 'blue',
  HIGH: 'red'
};

const STATUS_COLORS: Record<string, string> = {
  NEW: 'blue',
  IN_PROGRESS: 'gold',
  DONE: 'green',
  REJECTED: 'red'
};

function renderTag(value: string, map: Record<string, string>, fallbackLabel?: string) {
  const color = map[value] ?? 'default';
  return <Tag color={color}>{fallbackLabel ?? value}</Tag>;
}

export default function ChangeRequestsPage() {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editVisible, setEditVisible] = useState(false);
  const [selected, setSelected] = useState<ChangeRequest | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<ChangeRequest | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('page', String(page));
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      if (priorityFilter) {
        params.set('priority', priorityFilter);
      }
      if (searchQuery) {
        params.set('search', searchQuery);
      }

      const res = await api(`/api/change-requests?${params.toString()}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente ændringsønsker');
      }
      const data = await res.json();
      setRequests(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total ?? data.items?.length ?? 0));
    } catch (error: any) {
      console.error('Failed to load change requests', error);
      message.error(error?.message || 'Kunne ikke hente ændringsønsker');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, priorityFilter, searchQuery]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleSearch = useCallback(() => {
    setPage(1);
    setSearchQuery(searchInput.trim());
  }, [searchInput]);

  const handleCreate = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        title: values.title?.trim(),
        description: values.description?.trim(),
        priority: values.priority
      };
      setCreateLoading(true);
      const res = await api('/api/change-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette ændringsønske');
      }
      await res.json();
      message.success('Ændringsønske gemt');
      form.resetFields();
      setPage(1);
      loadRequests();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke oprette ændringsønske');
    } finally {
      setCreateLoading(false);
    }
  }, [form, loadRequests]);

  const openEditModal = useCallback(
    (record: ChangeRequest) => {
      setSelected(record);
      setEditVisible(true);
      editForm.setFieldsValue({
        status: record.status,
        priority: record.priority,
        admin_notes: record.admin_notes || ''
      });
    },
    [editForm]
  );

  useEffect(() => {
    if (editVisible && selected) {
      editForm.setFieldsValue({
        status: selected.status,
        priority: selected.priority,
        admin_notes: selected.admin_notes || ''
      });
    } else {
      editForm.resetFields();
    }
  }, [editVisible, selected, editForm]);

  const openViewModal = useCallback((record: ChangeRequest) => {
    setViewingRequest(record);
    setViewVisible(true);
  }, []);

  const handleEditSubmit = useCallback(async () => {
    if (!selected) {
      return;
    }
    try {
      const values = await editForm.validateFields();
      const payload = {
        status: values.status,
        priority: values.priority,
        adminNotes: values.admin_notes ?? null
      };
      setEditSubmitting(true);
      const res = await api(`/api/change-requests/${selected.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere ændringsønske');
      }
      await res.json();
      message.success('Ændringsønske opdateret');
      setEditVisible(false);
      setSelected(null);
      loadRequests();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere ændringsønske');
    } finally {
      setEditSubmitting(false);
    }
  }, [selected, editForm, loadRequests]);

  const columns = useMemo(() => {
    return [
      {
        title: 'Titel',
        dataIndex: 'title',
        key: 'title',
        render: (_: any, record: ChangeRequest) => (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Text strong>{record.title}</Text>
            <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
              {record.description}
            </Paragraph>
          </Space>
        )
      },
      {
        title: 'Prioritet',
        dataIndex: 'priority',
        key: 'priority',
        width: 120,
        render: (value: string) => renderTag(value, PRIORITY_COLORS, PRIORITY_OPTIONS.find((opt) => opt.value === value)?.label)
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        render: (value: string) => renderTag(value, STATUS_COLORS, STATUS_OPTIONS.find((opt) => opt.value === value)?.label)
      },
      {
        title: 'Indsendt af',
        dataIndex: 'requested_by_email',
        key: 'requested_by_email',
        width: 200,
        render: (_: any, record: ChangeRequest) => (
          <Space direction="vertical" size={0}>
            <Text>{record.requested_by_name || record.requested_by_email || 'Ukendt'}</Text>
            {record.requested_by_email && record.requested_by_name ? (
              <Text type="secondary">{record.requested_by_email}</Text>
            ) : null}
          </Space>
        )
      },
      {
        title: 'Oprettet',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (value: string) => formatDateTimeFull(value) || '—'
      },
      {
        title: 'Handlinger',
        key: 'actions',
        width: 140,
        render: (_: any, record: ChangeRequest) => (
          <Space size="middle">
            <Button type="link" onClick={() => openViewModal(record)}>
              Vis
            </Button>
            <Button type="link" onClick={() => openEditModal(record)}>
              Opdater
            </Button>
          </Space>
        )
      }
    ];
  }, [openEditModal, openViewModal]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      setPage(pagination.current ?? 1);
      setPageSize(pagination.pageSize ?? 10);
    },
    []
  );

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Card title="Nyt ændringsønske">
        <Form layout="vertical" form={form} autoComplete="off">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Titel"
                rules={[{ required: true, message: 'Angiv en titel' }]}
              >
                <Input placeholder="Kort titel" maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="priority"
                label="Prioritet"
                initialValue="MEDIUM"
                rules={[{ required: true, message: 'Vælg prioritet' }]}
              >
                <Select options={PRIORITY_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="description"
            label="Beskrivelse"
            rules={[{ required: true, message: 'Beskrivelse skal udfyldes' }]}
          >
            <Input.TextArea rows={4} placeholder="Beskriv ønsket og hvorfor det er vigtigt" />
          </Form.Item>
          <Button type="primary" onClick={handleCreate} loading={createLoading}>
            Gem ændringsønske
          </Button>
        </Form>
      </Card>

      <Card
        title="Registrerede ændringsønsker"
        extra={
          <Space>
            <Input.Search
              placeholder="Søg i titel eller tekst"
              allowClear
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onSearch={handleSearch}
              style={{ width: 240 }}
            />
            <Select
              placeholder="Filtrer status"
              allowClear
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
              style={{ width: 180 }}
            />
            <Select
              placeholder="Filtrer prioritet"
              allowClear
              value={priorityFilter}
              onChange={(value) => {
                setPriorityFilter(value);
                setPage(1);
              }}
              options={PRIORITY_OPTIONS}
              style={{ width: 160 }}
            />
            <Button onClick={loadRequests}>Opdater</Button>
          </Space>
        }
        bodyStyle={{ padding: 0 }}
      >
        <Table
          rowKey="id"
          dataSource={requests}
          columns={columns}
          loading={loading}
          locale={{ emptyText: loading ? <Empty description="Indlæser" /> : <Empty description="Ingen ændringsønsker" /> }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50']
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title={selected ? `Opdater "${selected.title}"` : 'Opdater ændringsønske'}
        open={editVisible}
        forceRender
        onCancel={() => {
          setEditVisible(false);
          setSelected(null);
        }}
        onOk={handleEditSubmit}
        confirmLoading={editSubmitting}
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} preserve={false}>
          <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Vælg status' }]}> 
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name="priority" label="Prioritet" rules={[{ required: true, message: 'Vælg prioritet' }]}> 
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
          <Form.Item name="admin_notes" label="Noter">
            <Input.TextArea rows={4} placeholder="Interne noter til opfølgning" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={viewingRequest ? viewingRequest.title : 'Detaljer'}
        open={viewVisible}
        onCancel={() => {
          setViewVisible(false);
          setViewingRequest(null);
        }}
        footer={null}
        destroyOnClose
      >
        {viewingRequest ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Titel">{viewingRequest.title}</Descriptions.Item>
            <Descriptions.Item label="Beskrivelse">
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{viewingRequest.description}</Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Prioritet">{renderTag(viewingRequest.priority, PRIORITY_COLORS, PRIORITY_OPTIONS.find((opt) => opt.value === viewingRequest.priority)?.label)}</Descriptions.Item>
            <Descriptions.Item label="Status">{renderTag(viewingRequest.status, STATUS_COLORS, STATUS_OPTIONS.find((opt) => opt.value === viewingRequest.status)?.label)}</Descriptions.Item>
            <Descriptions.Item label="Indsendt af">
              <Space direction="vertical" size={0}>
                <Text>{viewingRequest.requested_by_name || viewingRequest.requested_by_email || 'Ukendt'}</Text>
                {viewingRequest.requested_by_email && viewingRequest.requested_by_name ? (
                  <Text type="secondary">{viewingRequest.requested_by_email}</Text>
                ) : null}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Oprettet">{formatDateTimeFull(viewingRequest.created_at) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Opdateret">{formatDateTimeFull(viewingRequest.updated_at) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Admin noter">
              {viewingRequest.admin_notes ? (
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{viewingRequest.admin_notes}</Paragraph>
              ) : (
                <Text type="secondary">Ingen noter</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </Space>
  );
}
